import { useCallback, useEffect, useRef, useState } from 'react';

import { isApiEnabled, isMediaUploadEnabled } from '@muzly/api-client';
import type { NativeF0Frame } from '@muzly/shared';
import { buildReport, noteName } from '@muzly/voice-engine';
import type { ClipSet, Report } from '@muzly/voice-engine';

import {
  captureAttempts,
  isSpeakerRoute,
  rangeHz,
  registerStats,
  sustainStats,
  useVoiceCapture,
} from './capture';
import { useOnboarding } from './context';
import {
  DIAGNOSIS_PROGRESS_VERSION,
  clearProgress,
  downloadTakeFiles,
  loadDiagnosis,
  loadProgress,
  mergeCaptured,
  overallScore,
  resumedSource,
  saveDiagnosis,
  saveProgress,
  takeFiles,
  toDiagnosisRecord,
} from './record';
import {
  RECORD_START,
  SUSTAIN_IDLE_STOP_SEC,
  nextPosition,
  nextTestPosition,
  segmentAt,
  takeSpanSec,
  testAt,
} from './recordTests';
import { detectRegisterTransitions } from './registerTransition';
import { measuredRoomFloor } from './roomNoise';
import { songSegmentScore, sustainSegmentScore } from './segmentScore';
import {
  UPLOAD_TIMEOUT_MS,
  clearAnalySessionId,
  clearAnalySessionIdIf,
  createAnalySession,
  createAnalyTransport,
  ensureAnalySessionId,
  loadAnalySessionId,
  takeGraphSvg,
  toAnalyCapture,
  toAnalyGraph,
  toAnalyScores,
  toAnalySubmitBody,
  uploadTake,
} from './server';
import { scoreSong } from './songScoring';
import { toSurveyAnswers } from './survey';
import { sustainIdleSec } from './sustainGuide';
import { PCM_TARGET_SAMPLE_RATE } from '../../audio';

import type {
  CaptureAttempts,
  CaptureSource,
  FrameStats,
  LiveSample,
  MicError,
  SegmentId,
} from './capture';
import type { OnboardingAnswer } from './flow';
import type { MelodyNote } from './melody';
import type { ArchivedTake } from './record';
import type {
  CapturedDiagnosis,
  DiagnosisProgress,
  DiagnosisRecord,
} from './record';
import type { RecordPosition, RecordTest } from './recordTests';
import type {
  AnalyCapture,
  AnalyGraph,
  AnalyMetricsInput,
  AnalySession,
  AnalySessionResult,
  AnalyStereo,
  AnalySubmitBody,
} from './server';
import type { SongScore } from './songScoring';

/**
 * useRecordMachine — 진단 녹음의 상태기계.
 *
 * 한 진단이 세그먼트를 여럿 가질 수 있다(저음·고음). 위치는 `RecordPosition`
 * (진단 index + 세그먼트 index)이 들고, 시도는 여전히 **세그먼트 단위**로 쌓인다 —
 * 캡처·집계(framesToClips)가 보는 단위가 그거라서.
 *
 * 세그먼트마다 세션을 새로 열고 완료 버튼(또는 제한시간)으로 닫는다. 모든 진단을 마칠 때
 * **한 번에** buildReport로 넘긴다 — 진단별로 리포트를 만들면 음역이 저음/고음 각각의
 * 리포트로 흩어져 성부를 못 정한다.
 *
 * 종료 조건 셋이 기계에 있다.
 * ① **제한시간** — 음역은 20초, 지속음은 30초. 지나면 사람이 누른 것과 같은 문으로 끊는다.
 * ② **무진행 종료(지속음만)** — 발성이 끊긴 채 5초가 지나면 끝낸다(시안: "5초동안
 *    없으면 자동 종료"). MPT 진단이라 소리를 내는 동안은 어떤 음이든 진행 중이고,
 *    그대로 두면 숨이 끊긴 뒤 30초를 채울 때까지 화면이 멈춰 있는 것처럼 보인다.
 * ③ 사용자의 완료 버튼.
 * 셋 다 `stop()` 하나로 모인다 — 갈라지면 한쪽만 시도를 쌓는 상태가 생긴다.
 *
 * 화면은 VoiceRecordStep이 그린다 — 6단계 전이와 6개 화면을 한 컴포넌트에 두면 둘 다
 * 못 읽는다.
 */

export type RecordPhase =
  | 'guide'
  | 'countdown'
  | 'recording'
  | 'review'
  | 'done'
  | 'error';
export type RecordErrKind = MicError | 'invalid';
/** 세그먼트 id → 그 세그먼트의 시도들(시간순). 재시도는 이전 시도를 지우지 않는다. */
type Attempts = Partial<Record<SegmentId, NativeF0Frame[][]>>;

/** 3-2-1. 마이크를 켜자마자 재면 "준비" 없이 시작해 첫 소리를 놓친다. */
export const COUNTDOWN_FROM = 3;
const TICK_MS = 1000;
/** 경과 시간 갱신 주기 — 화면 타이머는 0.1초 자리까지 쓰지 않으므로 이걸로 충분. */
const ELAPSED_POLL_MS = 200;

export interface RecordMachine {
  phase: RecordPhase;
  err: RecordErrKind | null;
  /** 지금 진단과 그 안의 세그먼트 위치. */
  pos: RecordPosition;
  test: RecordTest;
  segment: SegmentId;
  /** 카운트다운 남은 수(3→1). `countdown` 단계에서만 의미 있다. */
  count: number;
  /** 이번 녹음의 벽시계 경과(초) — 화면 타이머와 제한시간 판정의 단일 출처. */
  elapsedSec: number;
  /** 측정이 멈춰 있나(시안 §5.1 `일시 중단`). 멈춘 동안 타이머도 프레임도 안 쌓인다. */
  paused: boolean;
  /** 지금 세그먼트의 시도들(시간순). */
  tries: NativeF0Frame[][];
  /**
   * 노래 따라부르기 채점 — **마지막 시도** 기준(시도를 합치지 않는다: 채점은 한 번의
   * 완창에 대한 것이고, 여러 시도의 좋은 부분을 이어 붙이면 측정이 아니라 고르기가 된다).
   * `undefined` = 아직 안 불렀거나 **측정 불가**(무성·무입력·레퍼런스 없음) — 0점이 아니다.
   */
  songScore: SongScore | undefined;
  /** 프레임이 어디서 왔나 — 에러 문구가 사용자를 탓할지 mock을 설명할지 가른다. */
  source: CaptureSource | null;
  live: LiveSample;
  /** 가이드 카드의 "시작" — 카운트다운을 연다. */
  begin: () => void;
  /** 측정을 잠깐 멈춘다. 테이크는 살아 있고 재개하면 그 뒤에 이어 붙는다. */
  pause: () => void;
  /** 다시 듣기 시작한다. */
  resume: () => void;
  stop: () => void;
  /**
   * 같은 세그먼트 다시 — 이전 시도는 남는다. 녹음 중에 부르면 **지금 테이크는 버리고**
   * (시도에 안 쌓인다, 원음 파일도 지운다) 카운트다운부터 다시 간다.
   */
  retrySegment: () => void;
  /** 다음 세그먼트/진단으로, 전부 끝났으면 리포트 생성. */
  nextSegment: () => void;
  /** 이 진단을 통째로 건너뛴다 — 성립할 수 없는 진단(레퍼런스 음원 실패)의 출구. */
  skipTest: () => void;
  /** 전체 다시 — 무효 리포트/에러에서만. */
  restartAll: () => void;
  /**
   * 무효 리포트에서 **지속음만** 다시 — 다른 진단은 그대로 두고 1번으로 돌아가, 확인하면
   * 곧장 마감한다. 리포트가 무효가 되는 원인은 지속음뿐이다(`buildReport`).
   */
  retrySustain: () => void;
  /** 지속음을 다시 재는 중 — 그 확인이 다음 진단이 아니라 마감으로 간다. */
  redoingSustain: boolean;
  /** 마감이 업로드를 거두는 중 — 그동안 확인·재녹음이 먹지 않는다. */
  finishing: boolean;
  /**
   * 이번 세션에서 가장 최근에 도착한 측정 테이크 녹음이 스피커 경로였나(`isSpeakerRoute`) —
   * 노래 진단 전 이어폰 권장의 근거. 녹음이 없으면(브라우저·복원 회차) false.
   */
  speakerRoute: boolean;
}

/**
 * 모은 시도(+재개한 복원본) → 리포트. `sustain`이 비어 있으면 `buildReport`가 null을 내
 * 그대로 재녹음으로 떨어진다 — 조용히 통과하는 경로는 없다.
 */
const reportFrom = (
  clips: ClipSet,
  answers: Record<string, OnboardingAnswer>,
): Report | null => buildReport(clips, toSurveyAnswers(answers));

/**
 * 서버 제출 순서. `song`이 마지막인 것이 계약이다 — 4종이 다 모인 제출에서 서버가 `done`으로
 * 전이하며 집계하므로(SPEC D20), 회차 산출물을 실은 제출이 그 자리에 있어야 한다.
 */
const ANALY_ORDER: AnalyStereo[] = ['sustain', 'low', 'high', 'song'];

/**
 * 마감이 업로드를 기다리는 상한. **업로드 한 건의 직렬 경로 전체**를 덮어야 한다 — 전송(PUT)
 * 하나의 상한(`UPLOAD_TIMEOUT_MS`)만으로는 모자란다. 한 건은 네이티브 녹음 RPC → 원음 읽기
 * RPC(`readTakeBytes`의 `READ_INVOKE` 12초) → 서명 → PUT(15초) → 등록 순으로 이어지고, 노래는
 * 그림 PUT(15초)이 끝나야 `mediaId` 가 풀린다(`uploadTake`가 `graphDone`을 기다린다). 각 단계가
 * 자기 예산 안에서 느린 것만으로 합이 15초를 넘는다.
 *
 * 넘는 것이 **손해인 이유**: 마감 제출이 회차를 `done` 으로 잠근 뒤에는 서버가 같은 하위의
 * `mediaId` 를 반영하지 않는다 — `capture` 만 바꾸고 나머지는 건드리지 않는다(백엔드
 * `submit-take.ts` D42). 늦게 끝난 업로드가 다시 보내도 그 테이크는 "내 녹음 다시 듣기"에서
 * 빠진다. 점수는 오디오와 무관하게 이미 나와 있어서 잃는 것은 그 재생뿐이지만, 한 번 놓치면
 * 되돌릴 수 없다.
 *
 * 값 = 읽기 12 + WAV PUT 15 + 그림 PUT 15 + 서명·등록 왕복과 녹음 RPC 의 통상 시간을 위한 여유 8초. 서명·등록
 * 요청에는 타임아웃이 없어 이 합으로도 못 덮는 경로가 남는다(그 경우는 `mediaId` 없이
 * 마감 — 사용자를 무한정 세우지 않는다). 대기는 업로드가 끝나는 즉시 풀리므로 이 값을 다
 * 쓰는 것은 네트워크가 멈춘 최악뿐이다.
 */
export const UPLOAD_WAIT_MS =
  12_000 + UPLOAD_TIMEOUT_MS + UPLOAD_TIMEOUT_MS + 8_000;

/** 클립 하나의 음역 끝 — 리포트와 같은 반올림·노트 이름. 잴 게 없으면 `undefined`. */
const takeEnd = (
  captured: CapturedDiagnosis,
  side: 'low' | 'high',
): { hz: number; note: string } | undefined => {
  const clip = captured.clips[side];
  const hz = clip ? rangeHz(clip, side) : null;
  return hz ? { hz: Math.round(hz), note: noteName(hz) } : undefined;
};

/**
 * 그때까지 잰 값 → 서버 지표 입력. **부분적인 것이 정상이다**: 음역은 저음·고음 클립이 둘 다
 * 있어야 나오므로 저음 테이크 직후에는 없다 — 그 대신 테이크 단독 끝음을 싣는다. 귀속되지
 * 않는 필드는 `toAnalySubmitBody`가 뺀다.
 */
const analyMetrics = (
  captured: CapturedDiagnosis,
  report: Report | null,
  songScore: SongScore | undefined,
): AnalyMetricsInput => {
  const low = takeEnd(captured, 'low');
  const high = takeEnd(captured, 'high');
  return {
    ...captured.stats,
    lowHz: low?.hz,
    lowNote: low?.note,
    highHz: high?.hz,
    highNote: high?.note,
    // 리포트 전에는 지속음 클립이 곧 MPT다(`metrics.ts`가 같은 `voicedSec`을 싣는다).
    // 0초는 "그때 소리가 없었다"가 아니라 아직 못 잰 것이라 필드를 뺀다.
    sustainSec:
      (report?.sustainSec ?? captured.clips.sustain.voicedSec) || undefined,
    range: report?.range ?? null,
    songScore,
  };
};

/**
 * 회차 마감 제출 넷. 테이크 경계에서 보낸 것을 최종값으로 덮어쓴다 — 그때는 음역이 아직
 * 없어 테이크 단독 끝음만 실렸고(음역은 저음·고음 클립이 둘 다 있어야 나온다) 산출물도
 * 리포트가 선 뒤라야 생긴다. 여기서는 리포트 음역이 그 끝음을 덮는다.
 * 같은 `stereo` 재제출은 서버가 복합키로 덮어써서 멱등이다(D18·C5).
 *
 * 산출물은 **마지막 하나**에만 싣는다: 4종이 다 모인 그 제출에서 `done` 전이와 집계가
 * 발화하므로(D20), 앞선 제출에 실으면 아직 집계할 수 없는 시점에 실린 채 버려진다.
 *
 * 테이크 `score`는 sustain·song만 싣는다(plan Decisions "low/high Segment Score" —
 * 귀속 가능한 축이 없다). sustain은 `overallScore(record)`(결과 화면 ScoreHero와 같은
 * 수), song은 `songSegmentScore`(accuracy 반올림). `graph$`는 sustain에만.
 */
const finalBodies = (
  captured: CapturedDiagnosis,
  report: Report,
  record: DiagnosisRecord,
  source: CaptureSource,
  sustainGraph: AnalyGraph | undefined,
  mediaIds: Partial<Record<AnalyStereo, string>>,
  /** 세그먼트 → `capture`. 네이티브 녹음이 있을 때만 실린다(`toAnalyCapture`). */
  captureOf: (stereo: AnalyStereo) => AnalyCapture | undefined,
): AnalySubmitBody[] => {
  const metrics = analyMetrics(captured, report, record.songScore);
  const session: AnalySessionResult = {
    scores: toAnalyScores(record.scores),
    pitchRegisters: record.pitchRegisters,
    fach: record.fach ?? undefined,
  };
  const scoreOf = (stereo: AnalyStereo): number | undefined => {
    switch (stereo) {
      case 'sustain':
        return overallScore(record) ?? undefined;
      case 'song':
        return songSegmentScore(record.songScore);
      default:
        return undefined;
    }
  };
  return ANALY_ORDER.map((stereo, index) =>
    toAnalySubmitBody(stereo, {
      metrics,
      source,
      score: scoreOf(stereo),
      // 없으면 키 자체가 빠진다(`defined<>`) — 업로드가 안 된 테이크는 `mediaId` 없이 간다.
      mediaId: mediaIds[stereo],
      ...(stereo === 'sustain' ? { graph: sustainGraph } : {}),
      capture: captureOf(stereo),
      ...(index === ANALY_ORDER.length - 1 ? { session } : {}),
    }),
  );
};

/**
 * 고음 세그먼트의 **마지막 시도**에서 센 성구 전환. 최댓값·최솟값을 고르지 않는 이유는
 * mergeSustain과 같다 — 여러 시도 중 마음에 드는 걸 고르는 건 측정이 아니라 고르기다.
 *
 * 이번 세션에 고음을 안 찍었으면(진행을 복원해 이어간 회차) `undefined`다: 복원본은
 * 프레임이 아니라 병합 클립만 남기므로(diagnosisProgress 모듈 주석 — 프레임은 WebView
 * 미러링에 너무 크다) 셀 프레임이 없다. 결과 화면은 그 회차를 **측정 불가**로 그린다.
 */
const highTransitions = (
  attempts: Partial<Record<SegmentId, NativeF0Frame[][]>>,
): number[] | undefined => {
  const tries = attempts.high ?? [];
  const last = tries[tries.length - 1];
  return last ? detectRegisterTransitions(last) : undefined;
};

/**
 * @param audioDurationMs 노래 음원의 길이(ms) — 테이크가 음원을 끝까지 덮게 한다.
 * @param melody 노래 따라부르기의 정답 멜로디(레퍼런스가 준비됐을 때만 들어온다). 채점을
 *   여기서 하는 이유: 캡처 프레임을 쥔 자리가 여기뿐이고(화면은 clip도 못 본다), 확인
 *   다이얼로그가 곧바로 그 값을 말해야 한다. 없으면 노래 채점은 측정 불가로 남는다.
 */
export const useRecordMachine = (
  melody?: MelodyNote[],
  audioDurationMs?: number,
): RecordMachine => {
  const { answers, setReport } = useOnboarding();
  // 재개할 진행은 마운트 때 한 번만 읽는다. 손상·구버전은 loadProgress가 버린다 →
  // null이면 그냥 처음부터다(사용자에게 실패를 알리지 않는다 — 알릴 것이 없다).
  const [resumed, setResumed] = useState<DiagnosisProgress | null>(
    loadProgress,
  );
  const {
    live,
    error,
    source,
    start: beginCapture,
    stop: stopCapture,
    pause: pauseCapture,
    resume: resumeCapture,
    markTake,
    takePcm,
    takeNativeRecording,
    readNoiseFloorDbfs,
    readNativeRecording,
    deleteNativeRecording,
    reset: resetCapture,
  } = useVoiceCapture();
  const [phase, setPhase] = useState<RecordPhase>('guide');
  const [err, setErr] = useState<RecordErrKind | null>(null);
  const [pos, setPos] = useState<RecordPosition>(resumed?.pos ?? RECORD_START);
  const [attempts, setAttempts] = useState<Attempts>({});
  const [count, setCount] = useState(COUNTDOWN_FROM);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [paused, setPaused] = useState(false);
  // 멈춘 순간까지 쌓인 초. 재개하면 여기서부터 다시 흐른다 — 매번 0에서 재면
  // 타이머가 뒤로 점프한다.
  const elapsedBaseRef = useRef(0);
  // 기본 발성을 다시 재다 끊긴 진행은 노래를 다시 안 부르므로 채점을 진행에서 되살린다.
  const [songScore, setSongScore] = useState<SongScore | undefined>(
    resumed?.redoSustain?.songScore,
  );
  // 세그먼트별 **마지막** 테이크의 원음. 재시도하면 덮어쓴다 — 리포트가 채택하는 것도
  // 마지막 시도라, 남기는 파일과 채점되는 값이 같은 녹음이어야 한다.
  const takesRef = useRef<Partial<Record<SegmentId, ArchivedTake>>>({});
  // 세그먼트별 게이트 바닥(재시도는 덮어쓴다). 녹음이 안 남은 테이크(RPC 실패)도 바닥은 쟀으므로
  // `takesRef` 와 따로 든다 — 결과 화면 소음 경고의 재료(`measuredRoomFloor`).
  const floorsRef = useRef<Partial<Record<SegmentId, number | null>>>({});
  // 가장 최근에 도착한 측정 테이크 녹음의 스피커 판정. 녹음은 확인 뒤 비동기로 도착하므로
  // ref 를 렌더에서 읽으면 늦게 온 테이크가 화면에 안 걸린다 — 도착할 때 상태로 올린다.
  const [speakerRoute, setSpeakerRoute] = useState(false);
  // 네이티브 원음은 RPC로 오므로 `stop()` 시점엔 아직 없다. 진단을 끝낼 때 **기다렸다가**
  // 내보내야 한다 — 마지막 세그먼트는 확인을 누른 그 순간 finish()가 도는지라, 안
  // 기다리면 그 테이크만 조용히 빠진다(세그먼트 사이 안내 화면이 없는 유일한 자리).
  const pendingTakesRef = useRef<Promise<void>[]>([]);
  /**
   * 세그먼트별 업로드 약속. 테이크가 끝나는 즉시 시작하고 마감이 결과를 거둔다.
   *
   * 회차마다 새로 선다 — 이 훅은 진단 화면을 벗어나면 언마운트되고, "새로 시작"은 그 경로로만
   * 온다. 그래서 버린 회차의 업로드가 다음 회차의 마감 제출에 섞일 자리가 없다.
   */
  const uploadsRef = useRef<
    Partial<Record<AnalyStereo, Promise<string | null>>>
  >({});
  /**
   * 테이크 경계에서 보낸 제출 본문과, 그 세그먼트의 업로드가 돌려준 `mediaId`.
   *
   * **둘이 만나는 시점이 정해져 있지 않아서** 양쪽에 둔다: 업로드는 `stop()` 직후 출발하고
   * 제출은 사용자가 확인을 누를 때 나가므로, 어느 쪽이 먼저 끝날지는 파일 크기와 회선이
   * 정한다. 늦게 도착한 쪽이 짝을 찾아 `mediaId`를 실은 제출을 한 번 더 보낸다 — 테이크
   * 제출은 복합키 덮어쓰기라 멱등이다(D18·C5).
   *
   * 이게 없으면 `mediaId`는 **마감 제출에만** 실린다(`finalBodies`). 중도 이탈한 회차는
   * 마감이 없어서, S3에 올라간 wav를 가리키는 레코드가 어디에도 안 남는다 — 지울 권한도
   * 없어 그대로 고아가 된다(2026-09-16 실측: `1000016-4` 회차의 테이크 3건 `mediaId=NONE`,
   * 같은 시각 S3에는 wav가 있었다).
   */
  const takeBodiesRef = useRef<Partial<Record<AnalyStereo, AnalySubmitBody>>>(
    {},
  );
  const mediaIdsRef = useRef<Partial<Record<AnalyStereo, string>>>({});
  /**
   * 세그먼트별 테이크 일련번호. 업로드를 걸 때 그 값을 닫아 두고, 돌아왔을 때 아직 같은
   * 번호인지 본다 — **재녹음이 짝을 가로채는 것을 막는 유일한 수단이다.** 버려진 테이크의
   * 업로드는 취소되지 않고 계속 돌아, 채택된 테이크보다 늦게 끝날 수 있다. 번호를 안 보면
   * 그 `mediaId` 가 채택된 테이크의 레코드에 실려 서버가 **다른 녹음**을 가리킨다.
   */
  const takeSeqRef = useRef<Partial<Record<AnalyStereo, number>>>({});
  /**
   * 마감이 업로드를 기다리는 동안 참. 그 사이 들어오는 확인·재녹음을 막는다.
   *
   * ref 와 state 를 같이 두는 이유: 막는 판정은 **그 자리에서** 읽어야 하고(state 는 다음
   * 렌더에나 보인다), 버튼을 흐리게 하는 것은 렌더에 실려야 한다.
   */
  const finishingRef = useRef(false);
  const [finishing, setFinishing] = useState(false);
  /**
   * 무효 리포트에서 지속음으로 돌아와 다시 재는 중인가.
   *
   * 예전 복구는 "마지막 측정만 다시"였는데, 마지막은 노래이고 리포트를 무효로 만드는 것은
   * 지속음뿐이라(`buildReport` 의 유일한 null) 몇 번을 다시 불러도 같은 에러로 돌아왔다.
   * 1번은 흔들려도 경고만 하고 넘겨 주므로(`attemptWarning`) 실기기에서도 난다. 이 플래그가
   * 서 있으면 지속음 확인이 저음으로 가지 않고 곧장 마감한다 — 나머지 셋은 이미 쟀다.
   */
  const [redoingSustain, setRedoingSustain] = useState(
    resumed?.redoSustain !== undefined,
  );
  // 지속음 파생 스칼라는 **테이크가 바뀔 때만** 잰다. 진행 저장은 세그먼트를 넘길 때마다
  // 일어나는데(`advance`) 그 사이 지속음 테이크는 그대로라, 캐시가 없으면 같은 프레임에서
  // 비브라토·호흡 분석이 한 회차에 서너 번 되풀이된다.
  const sustainCacheRef = useRef<{
    take: NativeF0Frame[] | undefined;
    stats: FrameStats;
  } | null>(null);
  const sustainStatsOf = (filled: CaptureAttempts): FrameStats => {
    const take = filled.sustain[filled.sustain.length - 1];
    const cached = sustainCacheRef.current;
    if (cached && cached.take === take) {
      return cached.stats;
    }
    const stats = sustainStats(filled);
    sustainCacheRef.current = { take, stats };
    return stats;
  };

  // 서버 세션은 회차 하나를 산다 — 밀린 제출 큐와 `done` 여부를 들고 있어서, 렌더마다 새로
  // 만들면 재시도 대상이 매번 사라진다. 백엔드 주소가 비면 전송기가 `null`이라 통째로 no-op다.
  // 브라우저 mock 회차도 같은 no-op — `createAnalySession`은 `transport: null`이면 큐에
  // 손도 안 대므로(analySession.ts `open()`), 별도 no-op 세션을 새로 만들 필요가 없다
  // (plan Decisions "mock 회차 서버 제출" — 제출 차단).
  const analyRef = useRef<AnalySession | null>(null);
  const analy = (): AnalySession =>
    (analyRef.current ??= createAnalySession({
      transport: recordSource() === 'mock' ? null : createAnalyTransport(),
      analyId: ensureAnalySessionId,
      // 404(루트 없음)를 받으면 저장된 id를 버리게 한다. 네이티브 셸이 localStorage를
      // AsyncStorage로 미러하고 부팅 때 되돌리므로, 마감 못 한 회차의 루트 id가 앱 재실행·
      // 웹 재배포·백엔드 교체를 넘어 살아남는다(injectedScripts `STORAGE_SYNC_SCRIPT`).
      resetAnalyId: clearAnalySessionId,
    }));

  const test = testAt(pos);
  const segment = segmentAt(pos);
  const tries = attempts[segment] ?? [];
  // 곡선의 가로축과 **같은 함수**를 읽는다 — 축이 안 덮는 구간까지 녹음하면 그 구간의
  // 점이 전부 화면 오른쪽 끝에 쌓인다.
  const limitSec = takeSpanSec(test, melody, audioDurationMs);

  // 캡처 에러(권한 거부/미지원) → 에러 화면.
  useEffect(() => {
    if (error) {
      setErr(error);
      setPhase('error');
    }
  }, [error]);

  // 이전 시도를 지우지 않고 쌓는다 — 합칠 때 MPT·음역은 최댓값을 쓰므로(framesToClips)
  // 못한 재시도가 이미 낸 기록을 깎지 않는다.
  // **시도가 쌓이는 유일한 자리**다: 사용자가 끊든 제한시간이 끊든 여기로 온다.
  const stop = useCallback((): void => {
    const frames = stopCapture();
    // 원음은 `stop()` **뒤에** 가져온다 — 브라우저는 마지막 청크까지 들어와야 하고,
    // 네이티브는 stop()에서 WAV 헤더를 마감한다.
    const take = takePcm();
    const recordedAt = Date.now();
    // 게이트 바닥은 **테이크 끝** 값이다 — 잠긴 뒤론 안 변하고, 못 잡았다면 null도
    // "못 잡았다"는 잰 사실이다.
    const noiseFloorDbfs = readNoiseFloorDbfs();
    floorsRef.current[segment] = noiseFloorDbfs;
    if (take) {
      takesRef.current[segment] = {
        segment,
        take,
        recording: null,
        frames,
        noiseFloorDbfs,
        recordedAt,
      };
    } else {
      // 네이티브 경로 — 파일은 기기에 있고 여기로는 경로만 온다. 확인 다이얼로그를 이
      // 응답에 묶지는 않는다(RPC가 늦으면 화면이 멈춘다). 대신 약속을 모아 두고
      // 내보내기 직전에 기다린다. 네이티브 캡처가 아니면 그 약속은 `null`뿐이라 모으지
      // 않는다 — 모으면 mock 회차의 마감·내보내기가 근거 없이 비동기가 된다.
      const pending = takeNativeRecording();
      if (source === 'native') {
        pendingTakesRef.current.push(
          pending
            .then((recording) => {
              if (recording) {
                if (segment !== 'song') {
                  setSpeakerRoute(isSpeakerRoute(recording));
                }
                takesRef.current[segment] = {
                  segment,
                  take: null,
                  recording,
                  frames,
                  noiseFloorDbfs,
                  recordedAt,
                };
              }
            })
            // 원음을 못 받는 것은 진단 실패가 아니다 — 리포트는 프레임으로 이미 선다.
            .catch(() => undefined),
        );
      }
      // 업로드는 **여기서 바로** 시작한다 — 마감까지 기다리면 네 개가 한꺼번에 몰려 결과
      // 화면 앞에서 그 시간을 전부 치른다. 재시도한 세그먼트는 나중 것이 앞의 약속을
      // 덮어써서, 리포트가 쓴 테이크와 올라간 파일이 같아진다(버려진 테이크의 S3 객체와
      // media 레코드는 고아로 남는다 — 지울 권한이 없다).
      // (`SegmentId`와 `AnalyStereo`는 같은 네 값이라 그대로 쓴다.)
      //
      // **올릴 데가 없으면 등록조차 하지 않는다.** 약속을 넣어 두면 마감이 늘 비동기가 되어
      // 브라우저·mock 회차까지 한 틱 늦어지고, 무엇보다 `readNativeRecording`이 불려 최대
      // 4MB를 WebView 힙으로 끌어온다 — 버킷이 없으면 그 바이트는 갈 데가 없다.
      //
      // **등록은 동기로 한다.** `.then` 안에서 넣으면 마지막 세그먼트가 빠진다 — 확인
      // 직후 곧바로 마감이 도는데 그때 이 약속이 아직 없다(`flushTakes`가 테이크에 대해
      // 겪은 것과 같은 함정).
      if (isMediaUploadEnabled()) {
        const seq = (takeSeqRef.current[segment] ?? 0) + 1;
        takeSeqRef.current[segment] = seq;
        uploadsRef.current[segment] = pending
          .then(async (recording) => {
            if (!recording) {
              return null;
            }
            const bytes = await readNativeRecording(recording.uri);
            if (!bytes) {
              return null;
            }
            const mediaId = await uploadTake({
              stereo: segment,
              body: bytes,
              // 실전 가창만 — 흐르는 창이라 화면으로는 테이크 전체를 되짚을 수 없는 유일한
              // 단계다. 그림은 여기서 만든다: 업로드가 꺼져 있으면 만들 일도 없다.
              graphSvg:
                segment === 'song'
                  ? takeGraphSvg({ frames, melody })
                  : undefined,
            });
            // 올라간 파일은 기기에서 지운다. 안 지우면 회차마다 쌓이고, 이 RPC 는 지금까지
            // 웹에 소비자가 하나도 없었다. 실패해도 진단과는 무관하다.
            //
            // **기다리지 않는다.** 삭제 결과는 아무도 안 읽는데, 기다리면 그 왕복 동안
            // 위 `bytes`(수 MB)가 이 프레임에 계속 붙어 있는다.
            if (mediaId !== null) {
              void deleteNativeRecording(recording.uri);
            }
            // 버려진 테이크의 업로드다 — 파일은 이미 S3 에 있지만(지울 권한이 없다) 짝은
            // 맺지 않는다. 마감이 읽는 `uploadsRef` 도 나중 약속이 이미 덮어썼다.
            if (takeSeqRef.current[segment] !== seq) {
              return mediaId;
            }
            if (mediaId !== null) {
              mediaIdsRef.current[segment] = mediaId;
              // 이 세그먼트의 테이크 제출이 이미 나갔으면 `mediaId`만 얹어 다시 보낸다.
              // 아직이면 `advance`가 첫 제출에 바로 싣는다.
              const sent = takeBodiesRef.current[segment];
              if (sent) {
                void analy().submitTake({ ...sent, mediaId });
              }
            }
            return mediaId;
          })
          .catch(() => null);
      }
    }
    setAttempts((prev) => ({
      ...prev,
      [segment]: [...(prev[segment] ?? []), frames],
    }));
    // 노래는 **여기서 바로 채점한다**: 프레임을 쥔 자리가 여기뿐이고, 확인 다이얼로그가
    // 곧바로 그 값을 말한다. 앵커는 첫 프레임이다 — 반주는 녹음 단계에 들어서는 순간
    // 걸리고(`VoiceRecordStep`) 프레임도 그 순간부터 모으므로(`markTake`) 둘이 같은 t=0 이다.
    if (segment === 'song') {
      setSongScore(melody ? scoreSong({ frames, melody }) : undefined);
    }
    setPhase('review');
  }, [
    segment,
    source,
    stopCapture,
    takePcm,
    takeNativeRecording,
    readNoiseFloorDbfs,
    readNativeRecording,
    deleteNativeRecording,
    melody,
  ]);

  // 세션은 **카운트다운에 들어서며** 연다. 3-2-1 의 3초가 게이트의 방 재기 창이다
  // (`voice-gate.ts` CALIBRATION_MS) — 녹음과 같이 열면 첫 프레임부터 부르는 사람의
  // 목소리가 창을 오염시켜 바닥을 못 잠그고, 노래 진단은 그 바닥 뒤에 반주를 걸므로
  // 반주가 "중간에" 나온다(2026-09-16 실기기). 카운트다운을 벗어나는 다른 길
  // (건너뛰기·다음 세그먼트·언마운트)은 각자 `resetCapture`/teardown 으로 세션을 닫는다.
  // 노래 진단만 반주가 같이 나간다 — 네이티브가 그때만 재생이 들리는 오디오 세션
  // 모드로 잡는다(`AudioInputConfig.backingTrack`).
  useEffect(() => {
    if (phase === 'countdown') {
      beginCapture({ backingTrack: test.id === 'song' });
    }
  }, [phase, beginCapture, test.id]);

  // 3-2-1 → 녹음 시작. 단계가 바뀌면 타이머를 걷어낸다 — 안 그러면 뒤로 간 화면에서
  // 녹음이 저절로 시작된다.
  useEffect(() => {
    if (phase !== 'countdown') {
      return;
    }
    const id = setTimeout(() => {
      if (count > 1) {
        setCount(count - 1);
        return;
      }
      setElapsedSec(0);
      elapsedBaseRef.current = 0;
      setPaused(false);
      // 카운트다운 동안 들은 것은 테이크가 아니다 — 여기서부터 모은다.
      markTake();
      setPhase('recording');
    }, TICK_MS);
    return () => clearTimeout(id);
  }, [phase, count, markTake]);

  // 벽시계 경과. `live.voicedSec`은 **소리를 낸** 시간이라 제한시간을 못 잰다 —
  // 20초 동안 아무 소리도 안 내면 영원히 안 끝난다.
  useEffect(() => {
    if (phase !== 'recording' || paused || live.takeInterrupted) {
      return;
    }
    const startedAt = Date.now();
    const base = elapsedBaseRef.current;
    const id = setInterval(
      () => setElapsedSec(base + (Date.now() - startedAt) / 1000),
      ELAPSED_POLL_MS,
    );
    return () => clearInterval(id);
  }, [phase, paused, live.takeInterrupted]);

  // 제한시간 도달 → 사용자가 누른 것과 같은 경로로 끊는다.
  //
  // **끊긴 테이크는 이 문으로 못 나간다.** `stop()`은 채점하고 리뷰로 넘기는 경로인데,
  // 인터럽션이 지나간 테이크는 반주가 흘러간 만큼 음이 어긋나 있다(`takeInterruption`).
  // 사용자가 초기화를 누르지 않아도 — 통화 중이라 화면을 아예 못 보고 있을 수 있다 —
  // 제한시간이 대신 눌러 주면 그 테이크가 조용히 채점·업로드된다. 끊긴 테이크가 나가는
  // 길은 `retrySegment` 하나뿐이다. 시계도 같이 서므로(위 효과) 여기 닿지도 않지만,
  // 이미 제한시간에 닿은 뒤 끊긴 경우가 있어 조건으로도 막는다.
  useEffect(() => {
    if (
      phase === 'recording' &&
      !paused &&
      !live.takeInterrupted &&
      limitSec != null &&
      elapsedSec >= limitSec
    ) {
      stop();
    }
  }, [phase, paused, live.takeInterrupted, elapsedSec, limitSec, stop]);

  // 지속음의 무진행 종료. 판정 근거는 **프레임**이지 벽시계가 아니다 — `live.points`의
  // 마지막 발성 시점 이후로 얼마나 지났는지를 본다. 벽시계로 재면 프레임이
  // 아예 안 오는 경우(마이크가 죽음)와 소리를 안 내는 경우를 구분하지 못한다.
  useEffect(() => {
    if (phase !== 'recording' || paused || test.viz !== 'sustain') {
      return;
    }
    // 재개 시각을 바닥으로 준다 — 멈춰 있는 동안에도 캡처 타임라인은 흐른다.
    if (
      sustainIdleSec(live.points, live.resumedAtMs ?? 0) >=
      SUSTAIN_IDLE_STOP_SEC
    ) {
      stop();
    }
  }, [phase, paused, test.viz, live.points, live.resumedAtMs, stop]);

  const begin = (): void => {
    setErr(null);
    setCount(COUNTDOWN_FROM);
    setPhase('countdown');
  };

  // 재개했으면 이번 세션 출처만으로는 회차를 판정할 수 없다 — 전송이 바뀐 채 이어간
  // 회차는 native임이 증명되지 않는다(resumedSource).
  const recordSource = (): CaptureSource =>
    resumedSource(resumed?.source ?? null, source);

  /** 세그먼트의 `capture` — 테이크 경계 제출과 마감 제출이 같은 조립을 쓴다. */
  const captureOf = (stereo: AnalyStereo): AnalyCapture | undefined =>
    toAnalyCapture(
      takesRef.current[stereo]?.recording ?? null,
      takesRef.current[stereo]?.noiseFloorDbfs ?? null,
    );

  /**
   * 모은 테이크를 파일로 내보낸다. 세그먼트마다가 아니라 **여기서 한 번에** — 재시도한
   * 세그먼트는 마지막 테이크만 남아 있어, 나가는 파일이 곧 리포트가 쓴 녹음이다.
   */
  const exportTakes = (): void => {
    const archived = Object.values(takesRef.current).filter(
      (t): t is ArchivedTake => t != null,
    );
    downloadTakeFiles(
      archived.flatMap((take) => takeFiles(take, PCM_TARGET_SAMPLE_RATE)),
    );
  };

  /**
   * 내보내기 한 번. 네이티브 경로는 원음 경로가 RPC로 오므로 **기다렸다가** 내보낸다:
   * 마지막 세그먼트는 확인 직후 `finish`가 도는지라, 안 기다리면 그 테이크만 조용히
   * 빠진다. 기다릴 것이 없으면 동기로 간다 — 브라우저·mock 경로를 마이크로태스크 뒤로
   * 밀면 리포트가 뜨는 시점이 근거 없이 한 틱 늦어진다.
   */
  const flushTakes = (): void => {
    const pending = pendingTakesRef.current;
    pendingTakesRef.current = [];
    if (pending.length > 0) {
      void Promise.all(pending).then(exportTakes);
    } else {
      exportTakes();
    }
  };

  /**
   * 진행 중인 업로드를 상한 안에서만 기다린다. 넘으면 그 테이크는 `mediaId` 없이 마감한다 —
   * 사용자를 결과 앞에 무한정 세우지 않는다. 원음은 점수와 무관하므로 잃는 것은 재생뿐이다.
   */
  const settleUploads = async (): Promise<
    Partial<Record<AnalyStereo, string>>
  > => {
    const entries = Object.entries(uploadsRef.current) as [
      AnalyStereo,
      Promise<string | null>,
    ][];
    if (entries.length === 0) {
      return {};
    }
    // 거둔 약속은 비운다 — 남겨 두면 다음 회차가 이 결과를 물려받는다.
    uploadsRef.current = {};
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), UPLOAD_WAIT_MS);
    });
    const settled = await Promise.all(
      entries.map(async ([stereo, promise]) => {
        const id = await Promise.race([promise, timeout]);
        return [stereo, id] as const;
      }),
    );
    // 다 끝났으면 타이머를 걷는다. 안 걷으면 상한 길이 타이머가 매 회차 하나씩 남는다.
    clearTimeout(timer);
    return Object.fromEntries(
      settled.filter((pair): pair is [AnalyStereo, string] => pair[1] != null),
    );
  };

  const finish = (
    collected: Attempts,
    mediaIds: Partial<Record<AnalyStereo, string>>,
  ): void => {
    const captured = mergeCaptured(resumed, collected, sustainStatsOf);
    const report = reportFrom(captured.clips, answers);
    if (!report) {
      // **무효로 끝난 회차야말로 원본이 필요하다.** 여기서 안 내보내면 그 회차의
      // 프레임·원음은 어디에도 남지 않는다 — 판정이 왜 떨어졌는지 나중에 물을 수 있는
      // 유일한 자료가 사라진다(2026-08-26 조사가 사용자 세션 대신 픽스처로 끝난 이유,
      // `.claude/20260826/DEBUG-16-13-22.md`). 성공 회차와 같은 파일이 나간다.
      flushTakes();
      setErr('invalid');
      setPhase('error');
      return;
    }
    // 서버 회차 id는 아래 `clearAnalySessionId`가 지우기 전에 레코드가 가져간다 — 재생·삭제·
    // 공유가 이 id로 서버를 부른다. 서버에 회차가 없는 경우(mock·API 꺼짐·루트를 못 만듦)는
    // 싣지 않는다: API가 꺼졌어도 네이티브 셸이 미러한 옛 id가 저장소에 남아 있을 수 있다.
    const analyId =
      recordSource() !== 'mock' && isApiEnabled()
        ? (loadAnalySessionId() ?? undefined)
        : undefined;
    const record = toDiagnosisRecord({
      report,
      answers,
      source: recordSource(),
      stats: captured.stats,
      // 노래 채점과 성구 전환은 **이 회차에 실제로 잰 것**만 실린다 — 건너뛴 진단은
      // 필드가 통째로 빠져 결과 화면이 "안 했다"고 말할 수 있다(0점·0회가 아니다).
      songScore,
      registerBreaks: highTransitions(collected),
      // 음역 구간은 **네 세그먼트의 프레임 전부**에서 나온다(지속음 하나로는 구간 하나만
      // 찬다). 경계는 이 회차가 잰 음역이 정하므로 리포트가 나온 뒤라야 계산할 수 있다.
      pitchRegisters: registerStats(captureAttempts(collected), report.range),
      completedAt: Date.now(),
      analyId,
      // mock 은 합성 프레임이라 바닥이 방이 아니다.
      roomFloorDbfs:
        recordSource() === 'mock'
          ? undefined
          : measuredRoomFloor(
              (['sustain', 'low', 'high'] as const).map(
                (segment) => floorsRef.current[segment],
              ),
            ),
    });
    // 출처를 리포트와 함께 싣는다 — 결과 화면이 나중에 `isRunningInNativeApp()`을 다시
    // 물으면 복원된 드래프트의 출처를 잘못 말하게 된다. 회차 레코드도 같이 싣는다:
    // 리포트와 채점이 동시에 존재하는 자리가 여기뿐이고('record'→'result'에서 이 훅은
    // 언마운트된다), 결과 화면이 그리는 값의 정본이 그 레코드다.
    setReport(report, recordSource(), record);
    // 진단이 끝난 순간 회차를 남긴다 — 로그인까지 왔는지와 별개로 진단은 일어난 사실이고,
    // 여기서 저장해야 프레임 출처(source)를 아는 자리에서 기록할 수 있다.
    // mock 세션은 saveDiagnosis가 걸러낸다.
    saveDiagnosis(record);
    // 회차를 서버에서도 닫는다. 마지막 제출이 `done` 전이와 집계를 발화하므로 이 한 번이
    // 그 회차를 서버에서 완성한다. 실패해도 화면은 기다리지 않는다 — 큐가 들고 있다.
    // `graph$`는 `sustain`의 마지막 시도에서 — 복원 회차는 이번 세션에 프레임이 없어
    // `collected.sustain`이 비고, 그러면 그래프 없이 나간다(plan Slice Queue 03).
    const sustainTake = collected.sustain?.[collected.sustain.length - 1];
    void analy()
      .submitFinal(
        finalBodies(
          captured,
          report,
          record,
          recordSource(),
          sustainTake ? toAnalyGraph(sustainTake) : undefined,
          mediaIds,
          captureOf,
        ),
      )
      .then(({ analyId: sentTo, stale }) => {
        // 마감 제출이 마감 중에 새 루트를 만들었다면(404 갱신) 그 생성은 위 `clear` 뒤에 끝나
        // 저장소에 남는다 — 끝난 회차의 루트라 다음 회차가 물려받으면 안 된다.
        if (sentTo !== null) {
          clearAnalySessionIdIf(sentTo);
        }
        // 마감이 위에서 읽은 id와 **다른 루트**에서 끝났을 수 있다 — 회차 중 루트를 못 만들어
        // 마감에서 처음 만들었거나, 404로 새 루트에서 마감했다. 서버가 없다고 확인한 루트는
        // 레코드에서 뺀다. 죽은 id나 빈 자리를 들고 있으면 재생·삭제·공유가 엉뚱한 곳을
        // 부른다. 보낼 곳이 없었던 마감(`null`·`stale` 아님)은 건드리지 않는다. 그 사이 지워진
        // 회차는 되살리지 않는다.
        const closedOn = stale ? undefined : (sentTo ?? record.analyId);
        if (closedOn !== record.analyId && loadDiagnosis(record.id)) {
          const { analyId: _read, ...rest } = record;
          saveDiagnosis(closedOn ? { ...rest, analyId: closedOn } : rest);
        }
      });
    flushTakes();
    // 끝난 진단의 진행이 남아 있으면 다음 진입이 스테일 위치에서 열린다.
    clearProgress();
    // 서버 루트도 이 회차와 함께 닫는다 — 남겨 두면 다음 진단이 같은 루트에 덮어쓴다.
    // 위 `submitFinal`은 이미 id를 쥐고 출발했으므로 여기서 지워도 그 제출은 온전히 나간다.
    clearAnalySessionId();
    setResumed(null);
    setPhase('done');
  };

  /**
   * 다음 자리로. `next`가 null이면 더 잴 게 없다는 뜻이라 그대로 분석으로 넘어간다.
   * 세그먼트 전진과 진단 건너뛰기가 **같은 문**을 쓴다 — 진행 저장·캡처 정리·다음 단계
   * 선택이 갈라지면 한쪽만 고쳐진 채 남는다.
   */
  const advance = (next: RecordPosition | null): void => {
    if (!next) {
      // 업로드를 먼저 재운다. `mediaId` 는 **마감 제출**에 실려야 한다 — 마지막 제출이
      // 회차를 `done` 으로 잠그고, 그 뒤 같은 하위에 다시 보내면 서버는 `capture` 만 바꾸고 `mediaId` 는
      // 버린다(`capture` 가 없으면 409 — 백엔드 `submit-take.ts` D42).
      //
      // 기다릴 것이 없으면 동기로 간다(`flushTakes`와 같은 규칙): 브라우저·mock 경로에는
      // 업로드가 애초에 없는데, 그것까지 마이크로태스크 뒤로 밀면 리포트가 뜨는 시점이
      // 근거 없이 한 틱 늦어진다.
      // **잠금을 먼저 본다.** 대기 동안 화면은 아직 확인 단계라 버튼이 살아 있고, 한 번 더
      // 눌리면 마감이 두 번 돈다(회차가 두 번 저장되고, 두 번째 제출은 이미 `done`인 루트라
      // 409다). 아래 "기다릴 것 없음" 분기보다 뒤에 두면 안 된다 — `settleUploads`가
      // 시작하며 `uploadsRef`를 비우므로, 두 번째 확인은 그 분기로 빠져나가 잠금을 지나친다.
      if (finishingRef.current) {
        return;
      }
      // 네이티브 녹음 RPC도 기다린다. 마지막 테이크는 확인 직후 여기로 오므로, 안 기다리면
      // 그 테이크의 마감 제출만 `capture` 없이 나간다. 상한은 RPC 타임아웃이 쥔다(실패는
      // `null`로 풀린다 — `takeNativeRecording`).
      const pendingTakes = pendingTakesRef.current;
      if (
        Object.keys(uploadsRef.current).length === 0 &&
        pendingTakes.length === 0
      ) {
        finish(attempts, {});
        return;
      }
      finishingRef.current = true;
      setFinishing(true);
      void Promise.all([settleUploads(), Promise.all(pendingTakes)]).then(
        ([mediaIds]) => {
          finishingRef.current = false;
          setFinishing(false);
          finish(attempts, mediaIds);
        },
      );
      return;
    }
    // 다음 화면에 직전 곡선이 남아 있으면 방금 부른 것처럼 보인다. 마지막에서는
    // **리셋하지 않는다**: reset이 `source`를 지우는데, 분석이 실패하면 에러 화면이 그
    // 값으로 문구를 고른다(브라우저 mock이면 사용자를 탓하지 않는 문구).
    resetCapture();
    setPos(next);
    setElapsedSec(0);
    // 진행은 **확인하고 넘어갈 때만** 남긴다. stop()마다 남기면 사용자가 아직 승인하지
    // 않은 재시도가 확정으로 기록되고, 재개가 넘어가지도 않은 자리에서 열린다.
    const captured = mergeCaptured(resumed, attempts, sustainStatsOf);
    saveProgress({
      version: DIAGNOSIS_PROGRESS_VERSION,
      pos: next,
      ...captured,
      source: recordSource(),
      updatedAt: Date.now(),
    });
    // 방금 마친 테이크를 서버에 남긴다 — 여기서 이탈해도 거기까지가 기록된다. **채택된
    // 테이크의 경계가 여기다**: `stop()` 뒤는 아직 `review`라 사용자가 다시 찍을 수 있고,
    // 그러면 버려진 테이크가 채택된 것보다 늦게 도착한다.
    // 건너뛴 진단은 시도가 없다 — 빈 테이크를 보내면 서버가 그 회차를 마친 것으로 센다.
    if (tries.length > 0) {
      // 세그먼트 점수는 sustain·song만(low/high는 귀속 가능한 축이 없다 — plan Decisions).
      // sustain은 이 시점 클립으로 다시 buildReport한 저장 축 평균, song은 accuracy 반올림.
      const segScore =
        segment === 'sustain'
          ? sustainSegmentScore(captured.clips, answers)
          : segment === 'song'
            ? songSegmentScore(songScore)
            : undefined;
      const takeBody = toAnalySubmitBody(segment, {
        metrics: analyMetrics(captured, null, songScore),
        source: recordSource(),
        score: segScore,
        // 네이티브 녹음이 도착해 있을 때만 실린다 — 늦은 RPC는 이 제출에 못 탄다.
        capture: captureOf(segment),
        // 업로드가 이 확인보다 빨랐으면 첫 제출에 바로 실린다. 늦으면 업로드 쪽이
        // `takeBodiesRef`에서 이 본문을 찾아 `mediaId`만 얹어 다시 보낸다.
        mediaId: mediaIdsRef.current[segment],
        ...(segment === 'sustain'
          ? { graph: toAnalyGraph(tries[tries.length - 1]) }
          : {}),
      });
      // 본문을 남겨 둔다 — 나중 도착한 `mediaId`가 실릴 자리가 여기뿐이다. 재녹음하면
      // 같은 키를 덮어써서, 짝은 **채택된** 테이크와 맺어진다.
      takeBodiesRef.current[segment] = takeBody;
      void analy().submitTake(takeBody);
    }
    // 진단이 바뀌면 가이드 카드를 다시 보여주고, 같은 진단 안의 다음 세그먼트(고음)는
    // 곧바로 카운트다운으로 잇는다 — 같은 안내를 두 번 읽힐 이유가 없다.
    if (next.testIndex !== pos.testIndex) {
      setPhase('guide');
      return;
    }
    setCount(COUNTDOWN_FROM);
    setPhase('countdown');
  };

  const retrySustain = (): void => {
    if (finishingRef.current) {
      return;
    }
    // 에러 화면을 떠나므로 캡처 상태를 지워도 된다(마감 직전 `advance` 가 리셋을 미룬 이유는
    // 에러 문구가 `source` 를 읽기 때문이었다).
    resetCapture();
    // 지속음 짝짓기 상태만 버린다 — `retrySegment` 와 같은 이유(버려진 테이크의 `mediaId` 가
    // 새 테이크에 실리지 않게). 이전 시도는 남고, 리포트는 마지막 시도를 채택한다.
    delete takeBodiesRef.current.sustain;
    delete mediaIdsRef.current.sustain;
    takeSeqRef.current.sustain = (takeSeqRef.current.sustain ?? 0) + 1;
    setRedoingSustain(true);
    // 진행도 이 자리로 남긴다 — 안 남기면 재측정 중에 앱이 죽었을 때 재개가 마지막으로
    // 확인한 자리(노래)에서 열리고, 노래를 다시 불러도 같은 무효로 돌아온다.
    saveProgress({
      version: DIAGNOSIS_PROGRESS_VERSION,
      pos: RECORD_START,
      ...mergeCaptured(resumed, attempts, sustainStatsOf),
      source: recordSource(),
      updatedAt: Date.now(),
      redoSustain: { songScore },
    });
    setPos(RECORD_START);
    setElapsedSec(0);
    setErr(null);
    // 다른 진단에서 돌아왔으니 안내를 다시 읽힌다(진단이 바뀌면 가이드 — `advance` 와 같은 규칙).
    setPhase('guide');
  };

  const restartAll = (): void => {
    // 마감이 업로드를 거두는 중이면 손대지 않는다 — 여기서 `attempts` 를 비우면 이미
    // 출발한 마감이 빈 시도로 리포트를 만든다.
    if (finishingRef.current) {
      return;
    }
    resetCapture();
    setReport(null);
    setAttempts({});
    takesRef.current = {};
    floorsRef.current = {};
    setSpeakerRoute(false);
    pendingTakesRef.current = [];
    // 버린 회차의 업로드도 버린다. 안 지우면 다시 찍지 않은 세그먼트가 **지난 회차의**
    // mediaId 를 마감 제출에 실어, 서버 기록이 다른 녹음을 가리킨다.
    uploadsRef.current = {};
    // 짝짓기 상태도 같이 버린다 — 남기면 다시 찍지 않은 세그먼트의 옛 본문·`mediaId`가
    // 새 회차의 업로드와 맺어져, 지난 녹음을 가리키는 제출이 한 번 더 나간다.
    takeBodiesRef.current = {};
    mediaIdsRef.current = {};
    // 번호는 **비우지 않고 올린다** — 0으로 되돌리면 아직 도는 업로드가 새 회차의 첫
    // 테이크와 같은 번호가 되어 짝을 가로챈다.
    for (const stereo of ANALY_ORDER) {
      takeSeqRef.current[stereo] = (takeSeqRef.current[stereo] ?? 0) + 1;
    }
    // 전체 다시 = 복원본도 버린다. 안 지우면 다시 찍지 않은 세그먼트에 지난 회차 클립이
    // 섞여 "처음부터"가 거짓이 된다.
    clearProgress();
    // **서버 루트는 지우지 않는다.** 여기는 인트로를 다시 거치지 않아 루트를 만들 자리가
    // 없다 — 지우면 다시 찍은 회차가 통째로 서버에 안 남는다. 테이크는 복합키라 같은 루트에
    // 덮어쓰면 되고(D18·C5), 그 루트는 아직 `pending`이다.
    setResumed(null);
    setPos(RECORD_START);
    setElapsedSec(0);
    setSongScore(undefined);
    setRedoingSustain(false);
    setErr(null);
    setPhase('guide');
  };

  return {
    phase,
    err,
    pos,
    test,
    segment,
    count,
    elapsedSec,
    paused,
    tries,
    songScore,
    source,
    live,
    begin,
    pause: () => {
      elapsedBaseRef.current = elapsedSec;
      pauseCapture();
      setPaused(true);
    },
    resume: () => {
      resumeCapture();
      setPaused(false);
    },
    stop,
    retrySegment: () => {
      // 마감이 업로드를 거두는 중이면 무시한다 — 이미 출발한 마감은 지금 시도를 쓰는데
      // 여기서 다시 찍기 시작하면 리포트와 화면이 갈린다.
      if (finishingRef.current) {
        return;
      }
      // **녹음 중이면 이 테이크를 버린다**(노래 진단의 `초기화`). 시도로 쌓지 않고 채점도 안
      // 한다 — 세션만 `stop()` 과 같은 순서로 닫고, 네이티브가 남긴 원음 파일을 지운다.
      // 업로드도 안 거는 테이크라 안 지우면 아무도 안 읽는 파일이 회차마다 기기에 쌓인다.
      // 경로는 `stop()` 처럼 세션을 닫은 **직후** 묻는다 — 새 세션은 카운트다운 효과가 다음
      // 렌더에 연다. 같은 틱의 연타는 같은 경로를 두 번 지우는데, 삭제는 멱등이다.
      if (phase === 'recording') {
        stopCapture();
        void takeNativeRecording().then((recording) => {
          if (recording) {
            void deleteNativeRecording(recording.uri);
          }
        });
      }
      // 이 세그먼트의 짝은 버린다. 앞 테이크의 업로드가 이미 끝나 있으면 그 `mediaId` 가
      // 남아 있고, 다음 확인이 그것을 실어 보낸다 — 버려진 녹음을 가리키는 레코드다.
      // 번호를 올려 두면 아직 도는 업로드도 돌아와서 짝을 못 맺는다.
      delete takeBodiesRef.current[segment];
      delete mediaIdsRef.current[segment];
      takeSeqRef.current[segment] = (takeSeqRef.current[segment] ?? 0) + 1;
      setElapsedSec(0);
      setCount(COUNTDOWN_FROM);
      setPhase('countdown');
    },
    // 지속음을 다시 재는 중이면 다음 진단이 아니라 마감이다 — 나머지는 이미 쟀다.
    nextSegment: () => advance(redoingSustain ? null : nextPosition(pos)),
    // 건너뛰기는 남은 세그먼트까지 통째로 넘긴다 — 못 들려준 노래의 두 번째 세그먼트를
    // 요구할 이유가 없다(지금 노래는 세그먼트가 하나지만, 규칙은 진단 단위다).
    skipTest: () => advance(nextTestPosition(pos)),
    restartAll,
    retrySustain,
    redoingSustain,
    finishing,
    speakerRoute,
  };
};
